import { hostTimeZone } from '@/lib/schedule';
import { useQueryClient } from '@tanstack/react-query';
import {
  CREDIT_PACK,
  PLAN_META,
  TRIAL_PACK,
  planPrice,
  type BillingInterval,
  type PlanKey,
} from '@str/shared';
import { useCallback, useRef, useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router';
import { CardFields, type ConfirmCard } from '@/components/CardFields';
import { Overlay } from '@/components/Overlay';
import { PlanPicker } from '@/components/PlanPicker';
import { Switch } from '@/components/Switch';
import { useToast } from '@/components/toast';
import { ErrorText } from '@/components/ui';
import { api, unwrap, errorMessage } from '@/lib/api';
import { authClient, authErrorMessage } from '@/lib/auth-client';
import { cardBrandLabel, cardExpiryLabel, cardExpiryState } from '@/lib/card-on-file';
import { initials, longDate, money, shortDate } from '@/lib/format';
import { useActiveProperty } from '@/lib/property-store';
import {
  accountApi,
  billingApi,
  keys,
  teamApi,
  useChannels,
  useInvoices,
  useMe,
  useTeam,
  useUpdateNotifications,
} from '@/lib/queries';
import './settings.css';

function UsageTile({
  label,
  value,
  pct,
  note,
}: {
  label: string;
  value: string;
  /** Share of a cap, when there is one; omitted for plain counts. */
  pct?: number;
  note?: string;
}) {
  return (
    <div
      className="card"
      style={{ display: 'flex', flexDirection: 'column', gap: 8, padding: '14px 16px' }}
    >
      <span style={{ fontSize: 12, color: '#888' }}>{label}</span>
      <span style={{ fontSize: 18, fontWeight: 800, letterSpacing: '-.02em', lineHeight: 1 }}>
        {value}
      </span>
      {pct !== undefined ? (
        <div
          style={{ height: 3, borderRadius: 99, background: 'rgba(0,0,0,.08)', overflow: 'hidden' }}
        >
          <span
            style={{
              display: 'block',
              height: '100%',
              width: `${Math.min(100, Math.max(0, pct))}%`,
              background: '#111',
              borderRadius: 99,
              transition: 'width .4s',
            }}
          />
        </div>
      ) : (
        <span style={{ fontSize: 11.5, color: '#999', lineHeight: 1 }}>{note ?? '\u00a0'}</span>
      )}
    </div>
  );
}

/** $449 for whole dollars, $74.83 otherwise. */
function dollars(cents: number) {
  const n = cents / 100;
  return Number.isInteger(n) ? `$${n}` : `$${n.toFixed(2)}`;
}

/** Every zone this browser knows; the property's own comes first when it is not among them. */
const ZONES: string[] = (() => {
  try {
    return (
      (Intl as unknown as { supportedValuesOf?: (k: string) => string[] }).supportedValuesOf?.(
        'timeZone',
      ) ?? []
    );
  } catch {
    return [];
  }
})();

export function Settings() {
  const me = useMe();
  const qc = useQueryClient();
  const toast = useToast();
  const updateNotifications = useUpdateNotifications();
  const navigate = useNavigate();
  const { active } = useActiveProperty();
  const channels = useChannels(active.id);
  const user = me.data?.user;
  const sub = me.data?.subscription;
  const [name, setName] = useState(user?.name ?? '');
  const [pw, setPw] = useState({ current: '', next: '' });
  const [pwError, setPwError] = useState<string | null>(null);
  const [billingError, setBillingError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [picker, setPicker] = useState(false);
  const [pickPlan, setPickPlan] = useState<PlanKey>(sub?.plan ?? 'starter');
  const [pickInterval, setPickInterval] = useState<BillingInterval>(sub?.interval ?? 'year');
  const [cancelOpen, setCancelOpen] = useState(false);
  const [startOpen, setStartOpen] = useState(false);
  const [archiving, setArchiving] = useState<string | null>(null);
  const [removing, setRemoving] = useState<string | null>(null);
  const [cardOpen, setCardOpen] = useState(false);
  const confirmRef = useRef<ConfirmCard | null>(null);
  const onCardReady = useCallback((fn: ConfirmCard) => {
    confirmRef.current = fn;
  }, []);
  const invoices = useInvoices(!!me.data?.subscription && me.data.billing.mode === 'stripe');
  const team = useTeam(active.id);
  const [inviteEmail, setInviteEmail] = useState('');
  const inviteLooksRight = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(inviteEmail.trim());
  const inviteError =
    inviteEmail.trim() && !inviteLooksRight
      ? 'Enter a full email address, like cohost@example.com'
      : null;
  const [teamError, setTeamError] = useState<string | null>(null);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState('');
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const refresh = () => qc.invalidateQueries({ queryKey: keys.me });

  if (!user) return null;

  const nameDirty = name.trim() !== user.name && name.trim().length > 0;
  const saveName = async (e: FormEvent) => {
    e.preventDefault();
    if (!nameDirty) return;
    setBusy('name');
    const res = await authClient.updateUser({ name: name.trim() });
    setBusy(null);
    if (res.error) return toast(authErrorMessage(res.error), { kind: 'error' });
    await refresh();
    toast('Name updated');
  };
  const changePassword = async (e: FormEvent) => {
    e.preventDefault();
    setPwError(null);
    setBusy('pw');
    const res = await authClient.changePassword({
      currentPassword: pw.current,
      newPassword: pw.next,
      revokeOtherSessions: true,
    });
    setBusy(null);
    if (res.error) return setPwError(authErrorMessage(res.error, 'Could not change password'));
    setPw({ current: '', next: '' });
    toast('Password changed');
  };
  const runBilling = async (key: string, fn: () => Promise<unknown>, msg: string) => {
    setBillingError(null);
    setBusy(key);
    try {
      await fn();
      await refresh();
      toast(msg);
      return true;
    } catch (err) {
      setBillingError(errorMessage(err));
      return false;
    } finally {
      setBusy(null);
    }
  };
  const teamAction = async (fn: () => Promise<unknown>, msg: string) => {
    setBusy('team');
    setTeamError(null);
    try {
      await fn();
      await qc.invalidateQueries({ queryKey: keys.team(active.id) });
      toast(msg);
      return true;
    } catch (err) {
      setTeamError(errorMessage(err));
      return false;
    } finally {
      setBusy(null);
    }
  };
  const sendInvite = async (e: FormEvent) => {
    e.preventDefault();
    const email = inviteEmail.trim();
    if (!inviteLooksRight) return;
    if (!email) return;
    if (await teamAction(() => teamApi.invite(active.id, email), `Invite sent to ${email}`))
      setInviteEmail('');
  };
  const signOut = async () => {
    await authClient.signOut();
    qc.clear();
    void navigate('/login', { replace: true });
  };
  const deleteAccount = async () => {
    setDeleteError(null);
    setBusy('delete');
    try {
      await accountApi.delete(deleteConfirm.trim());
    } catch (err) {
      setDeleteError(errorMessage(err));
      setBusy(null);
      return;
    }
    // The session died with the account; signing out only clears the cookie.
    await Promise.resolve(authClient.signOut()).catch(() => {});
    qc.clear();
    void navigate('/', { replace: true });
  };
  const deleteMatches =
    !!user && deleteConfirm.trim().toLowerCase() === user.email.trim().toLowerCase();

  const plan = sub?.plan ?? null;
  const meta = plan ? PLAN_META[plan] : null;
  const billed = sub ? sub.properties : [];
  const onPlan = billed.filter((p) => p.plan);
  const per = (tier: PlanKey) => (sub ? planPrice(tier, sub.interval).totalCents / 100 : 0);
  const unit = sub?.interval === 'year' ? 'a year' : 'a month';
  const tierLines = (['starter', 'pro'] as PlanKey[])
    .map((tier) => ({ tier, n: onPlan.filter((p) => p.plan === tier).length }))
    .filter((t) => t.n > 0);
  const planTotal = tierLines.reduce((sum, t) => sum + t.n * per(t.tier), 0);
  const planSum = tierLines
    .map((t) => `${t.n} × ${PLAN_META[t.tier].name} $${per(t.tier)} ${unit}`)
    .join(' + ');
  // The API's priceCents and perMonthCents are the whole subscription's figures; the unit is
  // what one property costs.
  const unitCents = sub ? (onPlan.length ? sub.priceCents / onPlan.length : sub.priceCents) : 0;
  // A comped account stores a $0 price: say so instead of dividing nothing across properties.
  const planLine = !sub
    ? ''
    : sub.priceCents === 0
      ? [
          'Complimentary',
          `${onPlan.length} ${onPlan.length === 1 ? 'property' : 'properties'}`,
        ].join(' · ')
      : tierLines.length > 1
        ? `${planSum} · $${planTotal} ${unit}`
        : [
            `${dollars(unitCents)} ${unit} per property`,
            onPlan.length > 1 ? `${onPlan.length} properties · $${planTotal} ${unit}` : null,
            onPlan.length === 1 ? '1 property' : null,
            sub.interval === 'year' ? `about ${dollars(sub.perMonthCents)} a month` : null,
          ]
            .filter(Boolean)
            .join(' · ');
  const properties = me.data?.properties ?? [];
  const usage = me.data?.usage ?? { used: 0 };
  const credits = me.data?.credits ?? null;
  const connected = (channels.data?.connections ?? []).filter(
    (c) => c.status === 'connected',
  ).length;
  const channelCap = meta?.channels.length ?? 0;
  // A trial's charge day is its end; the period end is the same day while trialing (R11).
  const trialChargeOn = sub?.trialEndsAt ?? sub?.currentPeriodEnd ?? null;
  const planNote = !sub
    ? ''
    : sub.status === 'past_due'
      ? 'Payment failed · update your card'
      : sub.cancelAtPeriodEnd
        ? `Cancels ${sub.currentPeriodEnd ? longDate(sub.currentPeriodEnd) : 'at period end'}`
        : sub.status === 'trialing'
          ? trialChargeOn
            ? `On ${longDate(trialChargeOn)} your card is charged $${sub.dayChargeCents / 100} for ${sub.interval === 'year' ? 'a year' : 'a month'} unless you cancel first.`
            : `On your trial · your card is charged $${sub.dayChargeCents / 100} when it ends unless you cancel first.`
          : sub.status === 'active' && sub.currentPeriodEnd
            ? `Renews ${longDate(sub.currentPeriodEnd)}`
            : sub.status.replace('_', ' ');
  const pickChanged = !!sub && (pickPlan !== sub.plan || pickInterval !== sub.interval);
  const confirmLabel = !sub
    ? ''
    : pickPlan !== sub.plan
      ? `Switch to ${PLAN_META[pickPlan].name}`
      : pickInterval !== sub.interval
        ? `Switch to ${pickInterval === 'year' ? 'annual' : 'monthly'} billing`
        : 'Keep current plan';
  const endsOn = sub?.currentPeriodEnd ?? sub?.trialEndsAt ?? null;
  const expiry = sub ? cardExpiryState(sub) : null;
  const saveCard = async () => {
    if (!confirmRef.current) return setBillingError('Card form is not ready yet');
    setBillingError(null);
    setBusy('card');
    try {
      const paymentMethodId = await confirmRef.current();
      await billingApi.updatePaymentMethod(paymentMethodId);
      await refresh();
      void qc.invalidateQueries({ queryKey: keys.invoices });
      setCardOpen(false);
      toast(sub?.status === 'past_due' ? 'Card updated · payment retried' : 'Card updated');
    } catch (err) {
      setBillingError(errorMessage(err));
    } finally {
      setBusy(null);
    }
  };

  return (
    <div
      className="a-rise"
      style={{ flex: 1, minHeight: 0, overflow: 'auto', position: 'relative' }}
    >
      <div className="set-page" style={{ display: 'flex', flexDirection: 'column' }}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            paddingBottom: 18,
          }}
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <div style={{ fontSize: 24, fontWeight: 800, letterSpacing: '-.03em' }}>Settings</div>
            <div style={{ fontSize: 13.5, color: '#777' }}>
              {user.name} · {sub?.planName ?? 'No plan'}
            </div>
          </div>
          <button
            type="button"
            className="btn-pill lg hv"
            style={{ color: '#777' }}
            onClick={() => void signOut()}
          >
            Sign out
          </button>
        </div>

        <div className="set-tiles">
          <UsageTile
            label="Properties"
            value={`${onPlan.length} on your plan`}
            note={`${properties.length} in your account`}
          />
          <UsageTile
            label="Credits"
            value={`${credits?.balance ?? 0}`}
            note={
              credits?.resetsAt
                ? `${meta?.creditsPerMonth ?? 0} a month · resets ${shortDate(credits.resetsAt)}`
                : sub?.status === 'trialing'
                  ? 'Trial pack · plan credits start with your plan'
                  : undefined
            }
          />
          <UsageTile
            label="Posts this month"
            value={`${usage.used}`}
            note="Scheduled or published"
          />
          <UsageTile
            label="Channels"
            value={`${connected} of ${channelCap || '—'}`}
            pct={channelCap ? (connected / channelCap) * 100 : 0}
          />
        </div>

        <section className="set-section">
          <h3>Account</h3>
          <form
            onSubmit={saveName}
            style={{ display: 'flex', flexDirection: 'column', gap: 14, minWidth: 0 }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <span className="avatar" style={{ width: 36, height: 36, fontSize: 12 }}>
                {initials(user.name) || 'H'}
              </span>
              <input
                className="set-input"
                value={name}
                onChange={(e) => setName(e.target.value)}
                aria-label="Name"
                placeholder="Your name"
                autoComplete="name"
              />
            </div>
            <div className="set-row sunken" style={{ flexWrap: 'wrap' }}>
              <span style={{ overflowWrap: 'anywhere', color: '#333' }}>{user.email}</span>
              <span
                style={{
                  fontSize: 11,
                  fontWeight: 700,
                  color: user.emailVerified ? '#2f6b3a' : '#8b3c1a',
                  background: user.emailVerified ? '#e8f2ea' : 'var(--accent-tint)',
                  padding: '2px 8px',
                  borderRadius: 99,
                  flex: 'none',
                }}
              >
                {user.emailVerified ? 'Verified' : 'Not verified'}
              </span>
              {!user.emailVerified && (
                <button
                  type="button"
                  className="set-link hv"
                  style={{ marginLeft: 'auto' }}
                  onClick={() =>
                    void authClient
                      .sendVerificationEmail({ email: user.email, callbackURL: '/app/settings' })
                      .then(() => toast('Verification email sent'))
                  }
                >
                  Resend email
                </button>
              )}
            </div>
            {nameDirty && (
              <div>
                <button
                  type="submit"
                  className="btn btn-primary hv a-rise"
                  style={{ padding: '9px 16px', fontSize: 13 }}
                  disabled={busy === 'name'}
                >
                  Save changes
                </button>
              </div>
            )}
          </form>
        </section>

        <section className="set-section">
          <h3>Delete account</h3>
          {deleteOpen ? (
            <form
              onSubmit={(e) => {
                e.preventDefault();
                if (deleteMatches) void deleteAccount();
              }}
              style={{ display: 'flex', flexDirection: 'column', gap: 12, minWidth: 0 }}
            >
              <span style={{ fontSize: 13, color: '#555', lineHeight: 1.5 }}>
                This removes every property, post, picture and reel you made here, right now. Your
                subscription ends. Posts already published on your social accounts stay there.
              </span>
              <input
                className="set-input"
                value={deleteConfirm}
                onChange={(e) => setDeleteConfirm(e.target.value)}
                aria-label="Type your email to confirm"
                placeholder={user.email}
                autoComplete="off"
                inputMode="email"
              />
              <ErrorText>{deleteError}</ErrorText>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                <button
                  type="submit"
                  className="btn btn-danger hv"
                  disabled={!deleteMatches || busy === 'delete'}
                >
                  {busy === 'delete' ? 'Deleting…' : 'Delete my account'}
                </button>
                <button
                  type="button"
                  className="btn-pill lg hv"
                  disabled={busy === 'delete'}
                  onClick={() => {
                    setDeleteOpen(false);
                    setDeleteConfirm('');
                    setDeleteError(null);
                  }}
                >
                  Keep my account
                </button>
              </div>
            </form>
          ) : (
            <div>
              <button type="button" className="set-link hv" onClick={() => setDeleteOpen(true)}>
                Delete account
              </button>
            </div>
          )}
        </section>

        <section className="set-section">
          <h3>People on {active.name}</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12, minWidth: 0 }}>
            <span style={{ fontSize: 12.5, color: '#777', lineHeight: 1.5 }}>
              Everyone here sees the same posts, calendar, media and channels.
              {active.role === 'member'
                ? ' The owner’s plan covers the work.'
                : ' Your plan covers their work.'}
            </span>
            {(team.data?.members ?? []).map((m) => (
              <div key={m.userId} className="set-row sunken" style={{ flexWrap: 'wrap' }}>
                <span className="avatar" style={{ width: 28, height: 28, fontSize: 10 }}>
                  {initials(m.name) || 'H'}
                </span>
                <span style={{ fontWeight: 600 }}>{m.name || m.email}</span>
                <span style={{ color: '#777', overflowWrap: 'anywhere' }}>{m.email}</span>
                <span
                  style={{
                    fontSize: 11,
                    fontWeight: 700,
                    color: '#555',
                    background: 'rgba(0,0,0,.06)',
                    padding: '2px 8px',
                    borderRadius: 99,
                    flex: 'none',
                  }}
                >
                  {m.role === 'owner' ? 'Owner' : 'Host'}
                </span>
                {active.role === 'owner' && m.role !== 'owner' && (
                  <button
                    type="button"
                    className="set-link hv"
                    style={{ marginLeft: 'auto' }}
                    disabled={busy === 'team'}
                    onClick={() =>
                      void teamAction(
                        () => teamApi.removeMember(active.id, m.userId),
                        `${m.name || m.email} removed`,
                      )
                    }
                  >
                    Remove
                  </button>
                )}
              </div>
            ))}
            {(team.data?.invites ?? []).map((i) => (
              <div key={i.id} className="set-row" style={{ flexWrap: 'wrap' }}>
                <span style={{ color: '#555', overflowWrap: 'anywhere' }}>{i.email}</span>
                <span style={{ fontSize: 12, color: '#999' }}>
                  Invited · link good until {longDate(i.expiresAt)}
                </span>
                <button
                  type="button"
                  className="set-link hv"
                  style={{ marginLeft: 'auto' }}
                  disabled={busy === 'team'}
                  onClick={() =>
                    void teamAction(
                      () => teamApi.revokeInvite(active.id, i.id),
                      `Invite to ${i.email} withdrawn`,
                    )
                  }
                >
                  Withdraw
                </button>
              </div>
            ))}
            <form onSubmit={sendInvite} style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <input
                className="set-input"
                type="email"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                aria-label="Invite by email"
                aria-invalid={inviteError ? true : undefined}
                aria-describedby={inviteError ? 'invite-email-error' : undefined}
                placeholder="cohost@example.com"
                autoComplete="off"
                style={{ flex: 1, minWidth: 200 }}
              />
              <button
                type="submit"
                className="btn btn-primary hv"
                style={{ padding: '9px 16px', fontSize: 13 }}
                disabled={busy === 'team' || !inviteLooksRight}
              >
                {busy === 'team' ? 'Sending…' : 'Invite'}
              </button>
              {inviteError && (
                <div id="invite-email-error" className="err" style={{ flexBasis: '100%' }}>
                  {inviteError}
                </div>
              )}
            </form>
            <ErrorText>{teamError}</ErrorText>
          </div>
        </section>

        <section className="set-section">
          <h3>Password</h3>
          <form
            onSubmit={changePassword}
            style={{ display: 'flex', flexDirection: 'column', gap: 10, minWidth: 0 }}
          >
            <div className="set-password">
              <input
                className="set-input"
                type="password"
                placeholder="Current password"
                autoComplete="current-password"
                value={pw.current}
                onChange={(e) => setPw({ ...pw, current: e.target.value })}
                aria-label="Current password"
                required
              />
              <input
                className="set-input"
                type="password"
                placeholder="New · 8+ characters"
                autoComplete="new-password"
                minLength={8}
                value={pw.next}
                onChange={(e) => setPw({ ...pw, next: e.target.value })}
                aria-label="New password"
                required
              />
              <button type="submit" className="btn-pill lg hv" disabled={busy === 'pw'}>
                Update
              </button>
            </div>
            <ErrorText>{pwError}</ErrorText>
          </form>
        </section>

        <section className="set-section" id="email">
          <h3>Email</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12, minWidth: 0 }}>
            <span className="set-note set-email-to">Sent to {user.email}.</span>
            <div className="set-row set-switch-row">
              <span className="set-row-text">
                <span id="email-post-live" className="set-switch-label">
                  A post went live
                </span>
                <span id="email-post-live-help" className="set-switch-help">
                  One email each time a scheduled post publishes, with a link to its results.
                </span>
              </span>
              <Switch
                labelId="email-post-live"
                descriptionId="email-post-live-help"
                checked={me.data?.notifications?.postLive ?? true}
                onChange={(postLive) =>
                  updateNotifications.mutate(
                    { postLive },
                    {
                      onError: (err) => toast(errorMessage(err), { kind: 'error' }),
                    },
                  )
                }
              />
            </div>
            <div className="set-row set-switch-row">
              <span className="set-row-text">
                <span id="email-calendar-nudges" className="set-switch-label">
                  Calendar reminders
                </span>
                <span id="email-calendar-nudges-help" className="set-switch-help">
                  An email when your calendar runs out or a week is empty, with the gaps and two
                  ideas to fill them. At most one a week.
                </span>
              </span>
              <Switch
                labelId="email-calendar-nudges"
                descriptionId="email-calendar-nudges-help"
                checked={me.data?.notifications?.calendarNudges ?? true}
                onChange={(calendarNudges) =>
                  updateNotifications.mutate(
                    { calendarNudges },
                    {
                      onError: (err) => toast(errorMessage(err), { kind: 'error' }),
                    },
                  )
                }
              />
            </div>
            <span className="set-note">
              We always email you when a post does not go out or we can’t confirm it, and for
              sign-in, invites and billing.
            </span>
          </div>
        </section>

        <section className="set-section">
          <h3>Plan &amp; billing</h3>
          {sub ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14, minWidth: 0 }}>
              <div className="set-row set-plan-row">
                <span className="k">Plan</span>
                <span style={{ fontWeight: 700 }}>
                  {tierLines.length > 1
                    ? tierLines.map((t) => PLAN_META[t.tier].name).join(' + ')
                    : sub.planName}
                </span>
                <span style={{ color: '#777' }}>{planLine}</span>
                <span
                  className="set-plan-note"
                  style={{
                    color: sub.cancelAtPeriodEnd || sub.status === 'past_due' ? '#8b3c1a' : '#999',
                    fontSize: 12.5,
                  }}
                >
                  {planNote}
                </span>
                <button
                  type="button"
                  className="btn-pill hv set-plan-change"
                  onClick={() => {
                    setPickPlan(sub.plan);
                    setPickInterval(sub.interval);
                    setPicker((o) => !o);
                  }}
                >
                  {picker ? 'Close' : 'Change plan'}
                </button>
              </div>
              {picker && (
                <div
                  className="a-rise"
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 12,
                    padding: 14,
                    borderRadius: 14,
                    background: 'var(--bg)',
                  }}
                >
                  <PlanPicker
                    value={pickPlan}
                    onChange={setPickPlan}
                    interval={pickInterval}
                    onIntervalChange={setPickInterval}
                    billedWhen="at renewal"
                  />
                  <span style={{ fontSize: 12, color: '#777' }}>
                    {onPlan.length === 1
                      ? 'Your property moves to this tier.'
                      : onPlan.length === 2
                        ? 'Both properties move to this tier.'
                        : `All ${onPlan.length} properties move to this tier.`}
                  </span>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <button
                      type="button"
                      className="btn btn-primary hv"
                      style={{ padding: '8px 14px', fontSize: 12.5 }}
                      disabled={!pickChanged || busy === 'plan'}
                      onClick={() =>
                        void runBilling(
                          'plan',
                          () => billingApi.changePlan(pickPlan, pickInterval),
                          `Switched to ${PLAN_META[pickPlan].name} · ${pickInterval === 'year' ? 'annual' : 'monthly'}`,
                        ).then((ok) => ok && setPicker(false))
                      }
                    >
                      {confirmLabel}
                    </button>
                    <span style={{ fontSize: 12.5, color: '#999' }}>
                      {pickChanged
                        ? 'Takes effect right away · prorated by Stripe'
                        : "You're on this plan now"}
                    </span>
                  </div>
                </div>
              )}
              <div className="set-row" style={{ flexWrap: 'wrap' }}>
                <span className="k">Properties</span>
                <span style={{ fontSize: 12.5 }}>
                  {planSum ? `${planSum} = $${planTotal} ${unit}` : 'None on your plan yet'}
                  {sub.currentPeriodEnd && sub.status === 'active'
                    ? ` · renews ${longDate(sub.currentPeriodEnd)}`
                    : ''}{' '}
                  · USD
                </span>
              </div>
              {billed.map((p) =>
                removing === p.id ? (
                  <div key={p.id} className="set-row" style={{ flexWrap: 'wrap' }}>
                    <span style={{ fontSize: 12.5 }}>
                      Stops the ${per(p.plan!)} {unit} for {p.name} at your next{' '}
                      {sub.status === 'trialing' ? 'charge' : 'renewal'}
                      {sub.currentPeriodEnd ? ` on ${longDate(sub.currentPeriodEnd)}` : ''}.{' '}
                      {p.name} stays in your account; posting and generation from it stop now until
                      you add it back.
                    </span>
                    <button
                      type="button"
                      className="btn-pill hv"
                      style={{ marginLeft: 'auto' }}
                      disabled={busy === 'detach'}
                      onClick={() =>
                        void runBilling(
                          'detach',
                          () => billingApi.detachPlan(p.id),
                          `${p.name} is off your plan from your next renewal`,
                        ).then(() => setRemoving(null))
                      }
                    >
                      Yes, remove it
                    </button>
                    <button type="button" className="btn-pill hv" onClick={() => setRemoving(null)}>
                      Keep it
                    </button>
                  </div>
                ) : (
                  <div key={p.id} className="set-row" style={{ flexWrap: 'wrap' }}>
                    <span style={{ fontWeight: 600 }}>{p.name}</span>
                    <span style={{ color: '#777', fontSize: 12.5 }}>
                      {p.plan
                        ? `${PLAN_META[p.plan].name} · $${per(p.plan)} ${unit}`
                        : 'Not on your plan · no posting or generation'}
                    </span>
                    {p.plan ? (
                      onPlan.length > 1 && (
                        <button
                          type="button"
                          className="btn-pill hv"
                          style={{ marginLeft: 'auto' }}
                          aria-label={`Remove ${p.name} from your plan`}
                          onClick={() => setRemoving(p.id)}
                        >
                          Remove
                        </button>
                      )
                    ) : (
                      <button
                        type="button"
                        className="btn-pill hv"
                        style={{ marginLeft: 'auto' }}
                        aria-label={`Add ${p.name} to your plan`}
                        onClick={() => void navigate(`/onboarding/plan?property=${p.id}`)}
                      >
                        Add to plan
                      </button>
                    )}
                  </div>
                ),
              )}
              {sub.status === 'trialing' && (
                <div className="set-row set-wrap-row">
                  <span className="k">Trial</span>
                  {startOpen ? (
                    <>
                      <span className="set-row-text" style={{ fontSize: 12.5 }}>
                        Your card is charged ${sub.dayChargeCents / 100} today for {sub.planName}{' '}
                        {sub.interval === 'year' ? 'annual' : 'monthly'}. Your monthly credits land
                        now.
                      </span>
                      <button
                        type="button"
                        className="btn-pill hv set-row-action"
                        disabled={busy === 'start'}
                        onClick={() =>
                          void runBilling(
                            'start',
                            billingApi.startPlanNow,
                            'Your plan has started',
                          ).then(() => setStartOpen(false))
                        }
                      >
                        Yes, start my plan
                      </button>
                      <button
                        type="button"
                        className="btn-pill hv set-row-action"
                        onClick={() => setStartOpen(false)}
                      >
                        Not now
                      </button>
                    </>
                  ) : (
                    <>
                      <span className="set-row-text" style={{ color: '#777', fontSize: 12.5 }}>
                        Want your monthly credits before then? Start the plan early.
                      </span>
                      <button
                        type="button"
                        className="btn-pill hv set-row-action"
                        onClick={() => setStartOpen(true)}
                      >
                        Start my plan now · ${sub.dayChargeCents / 100} today
                      </button>
                    </>
                  )}
                </div>
              )}
              <div className="set-row set-plan-row">
                <span className="k">Credits</span>
                <span style={{ fontWeight: 700 }}>{credits?.balance ?? 0}</span>
                <span className="set-plan-note" style={{ color: '#777', fontSize: 12.5 }}>
                  {credits?.resetsAt
                    ? `${meta?.creditsPerMonth ?? 0} a month with ${sub.planName} · resets ${shortDate(credits.resetsAt)} · packs never expire`
                    : 'Trial pack · packs never expire'}
                </span>
                <button
                  type="button"
                  className="btn-pill hv set-plan-change"
                  disabled={busy === 'pack'}
                  onClick={() =>
                    void runBilling(
                      'pack',
                      () => billingApi.buyPack(1),
                      `${CREDIT_PACK.credits} credits added`,
                    )
                  }
                >
                  Buy {CREDIT_PACK.credits} credits for ${CREDIT_PACK.priceCents / 100}
                </button>
                {me.data?.features.devMailbox && (
                  <button
                    type="button"
                    className="btn-pill hv"
                    disabled={busy === 'test-credits'}
                    title="Preview and local only: this button does not exist in production"
                    onClick={() =>
                      void runBilling(
                        'test-credits',
                        () => billingApi.addTestCredits(100),
                        '100 test credits added',
                      )
                    }
                  >
                    Add 100 test credits
                  </button>
                )}
              </div>
              <div className="set-row set-wrap-row">
                <span className="k">Payment</span>
                <span
                  aria-hidden="true"
                  style={{
                    width: 26,
                    height: 17,
                    borderRadius: 4,
                    background: 'var(--brand-ink)',
                    display: 'inline-block',
                    flex: 'none',
                  }}
                />
                {sub.paymentLast4 ? (
                  <>
                    <span>
                      {cardBrandLabel(sub.cardBrand)} ···· {sub.paymentLast4}
                    </span>
                    {cardExpiryLabel(sub) && (
                      <span style={{ color: expiry === 'ok' ? '#999' : '#8b3c1a', fontSize: 12.5 }}>
                        {expiry === 'expired' ? 'Expired' : 'Exp'} {cardExpiryLabel(sub)}
                        {expiry === 'expiring' ? ' · expiring soon' : ''}
                      </span>
                    )}
                  </>
                ) : (
                  <span>No card on file</span>
                )}
                {me.data?.billing.mode === 'mock' && (
                  <span style={{ color: '#999', fontSize: 12.5 }}>Test mode</span>
                )}
                <button
                  type="button"
                  className="btn-pill hv set-row-action"
                  onClick={() => setCardOpen((o) => !o)}
                >
                  {cardOpen ? 'Close' : sub.paymentLast4 ? 'Update card' : 'Add card'}
                </button>
              </div>
              {cardOpen && (
                <div
                  className="a-rise"
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 10,
                    padding: 14,
                    borderRadius: 14,
                    background: 'var(--bg)',
                  }}
                >
                  <CardFields
                    mode={me.data?.billing.mode ?? 'mock'}
                    publishableKey={me.data?.billing.publishableKey ?? null}
                    onReady={onCardReady}
                  />
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <button
                      type="button"
                      className="btn btn-primary hv"
                      style={{ padding: '8px 14px', fontSize: 12.5 }}
                      disabled={busy === 'card'}
                      onClick={() => void saveCard()}
                    >
                      {busy === 'card' ? 'Saving…' : 'Save card'}
                    </button>
                    <span style={{ fontSize: 12.5, color: '#999' }}>
                      {sub.status === 'past_due'
                        ? 'We retry the failed payment with this card right away.'
                        : 'Replaces the card on file · future charges use this card.'}
                    </span>
                  </div>
                </div>
              )}
              <ErrorText>{billingError}</ErrorText>
              <div style={{ display: 'flex', gap: 16, padding: '0 2px' }}>
                {sub.cancelAtPeriodEnd ? (
                  <button
                    type="button"
                    className="set-link hv"
                    disabled={busy === 'cancel'}
                    onClick={() =>
                      void runBilling('cancel', billingApi.resume, 'Subscription resumed')
                    }
                  >
                    Resume plan
                  </button>
                ) : (
                  <button type="button" className="set-link hv" onClick={() => setCancelOpen(true)}>
                    Cancel plan
                  </button>
                )}
              </div>
            </div>
          ) : (
            <div>
              <Link
                to="/onboarding/plan"
                className="btn btn-primary hv"
                style={{ alignSelf: 'flex-start' }}
              >
                Choose a plan
              </Link>
            </div>
          )}
          <p className="set-legal">
            <a href="https://str-leads.com/terms" target="_blank" rel="noreferrer">
              Terms
            </a>
            {' · '}
            <a href="https://str-leads.com/privacy" target="_blank" rel="noreferrer">
              Privacy
            </a>
            {' · '}
            <a href="mailto:support@str-leads.com">support@str-leads.com</a>
          </p>
        </section>

        <section className="set-section">
          <h3>Manage properties</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10, minWidth: 0 }}>
            {(me.data?.properties ?? [])
              .filter((p) => p.role === 'owner')
              .map((p) => (
                <div key={p.id} className="set-row" style={{ flexWrap: 'wrap' }}>
                  <span style={{ fontWeight: 600 }}>{p.name}</span>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12.5 }}>
                    <span style={{ color: '#777' }}>Clock</span>
                    <select
                      aria-label={`Time zone for ${p.name}`}
                      value={p.timezone ?? ''}
                      onChange={(e) =>
                        void runBilling(
                          'zone',
                          async () => {
                            await unwrap(
                              api.api.onboarding.properties[':id'].$patch({
                                param: { id: p.id },
                                json: { timezone: e.target.value || null },
                              }),
                            );
                          },
                          'Time zone saved',
                        )
                      }
                      style={{
                        font: 'inherit',
                        fontSize: 12.5,
                        padding: '4px 6px',
                        borderRadius: 8,
                      }}
                    >
                      <option value="">Browser's zone ({hostTimeZone()})</option>
                      {ZONES.map((z) => (
                        <option key={z} value={z}>
                          {z.replace(/_/g, ' ')}
                        </option>
                      ))}
                    </select>
                  </label>
                  {archiving === p.id ? (
                    <>
                      <span style={{ width: '100%', fontSize: 12, color: '#777' }}>
                        Remove this property from your account? Its photos and history are retained.{' '}
                        {p.plan
                          ? 'Its plan charge stops at the next renewal. The last paid property requires cancelling your plan first.'
                          : ''}
                      </span>
                      <button
                        className="btn-pill"
                        disabled={busy === 'archive'}
                        onClick={() =>
                          void runBilling(
                            'archive',
                            async () => {
                              await unwrap(
                                api.api.properties[':id'].$delete({ param: { id: p.id } }),
                              );
                              setArchiving(null);
                            },
                            'Property removed from your account',
                          )
                        }
                      >
                        Remove property
                      </button>
                      <button
                        className="btn-pill"
                        disabled={busy === 'archive'}
                        onClick={() => setArchiving(null)}
                      >
                        Keep property
                      </button>
                    </>
                  ) : (
                    <button
                      className="btn-pill"
                      onClick={() => setArchiving(p.id)}
                      aria-label={`Remove ${p.name} from account`}
                    >
                      Remove from account
                    </button>
                  )}
                </div>
              ))}
            {!sub && <ErrorText>{billingError}</ErrorText>}
          </div>
        </section>

        {sub && (
          <section className="set-section">
            <h3>Billing history</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, minWidth: 0 }}>
              {me.data?.billing.mode !== 'stripe' ? (
                <span style={{ fontSize: 12.5, color: '#999' }}>
                  Invoices appear here once billing runs through Stripe.
                </span>
              ) : invoices.isPending ? (
                <span style={{ fontSize: 12.5, color: '#999' }}>Loading invoices…</span>
              ) : invoices.data?.length ? (
                invoices.data.map((inv) => (
                  <div key={inv.id} className="set-row" style={{ gap: 12 }}>
                    <span style={{ color: '#777', fontSize: 12.5, flex: 'none', width: 92 }}>
                      {longDate(inv.createdAt)}
                    </span>
                    <span
                      style={{
                        flex: 1,
                        minWidth: 0,
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      {inv.description ?? inv.number ?? 'Invoice'}
                    </span>
                    <span style={{ fontWeight: 700 }}>
                      ${(inv.amountCents / 100).toFixed(2)} {inv.currency.toUpperCase()}
                    </span>
                    <span
                      style={{
                        fontSize: 11,
                        fontWeight: 700,
                        padding: '2px 8px',
                        borderRadius: 99,
                        background: inv.status === 'paid' ? '#e8f2ea' : 'var(--accent-tint)',
                        color: inv.status === 'paid' ? '#2f6b3a' : '#8b3c1a',
                        flex: 'none',
                      }}
                    >
                      {inv.status === 'paid' ? 'Paid' : inv.status === 'open' ? 'Due' : inv.status}
                    </span>
                    {(inv.hostedUrl ?? inv.pdfUrl) && (
                      <a
                        href={inv.hostedUrl ?? inv.pdfUrl ?? '#'}
                        target="_blank"
                        rel="noreferrer"
                        className="set-link hv"
                        style={{ textDecoration: 'none' }}
                      >
                        {inv.status === 'open' ? 'Pay' : 'Receipt'}
                      </a>
                    )}
                  </div>
                ))
              ) : (
                <span style={{ fontSize: 12.5, color: '#999' }}>
                  No invoices yet · your first one arrives when the trial ends.
                </span>
              )}
            </div>
          </section>
        )}
        <div style={{ borderTop: '1px solid var(--line)' }} />
        {me.data?.features.devMailbox && (
          <div style={{ marginTop: 16, fontSize: 12, color: '#999' }}>
            Dev: <Link to="/mailbox">open the local mailbox</Link> to see verification and reset
            emails.
          </div>
        )}
      </div>

      {cancelOpen && sub && (
        <Overlay onClose={() => setCancelOpen(false)}>
          <div
            role="dialog"
            aria-labelledby="cancel-title"
            className="a-rise"
            style={{
              width: 400,
              maxWidth: 'calc(100% - 48px)',
              background: '#fff',
              borderRadius: 20,
              padding: 24,
              display: 'flex',
              flexDirection: 'column',
              gap: 18,
              boxShadow: '0 40px 80px -30px rgba(0,0,0,.5)',
            }}
          >
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <span
                id="cancel-title"
                style={{ fontSize: 18, fontWeight: 800, letterSpacing: '-.025em' }}
              >
                Cancel your plan?
              </span>
              <span style={{ fontSize: 13.5, color: '#777', lineHeight: 1.5 }}>
                {sub.status !== 'trialing'
                  ? ''
                  : sub.trialPackPaid
                    ? `Your ${money(TRIAL_PACK.priceCents)} trial pack stays yours and nothing more is charged; you keep what you made. `
                    : 'Nothing more is charged. '}
                {sub.planName} stays on until{' '}
                <b style={{ color: '#111', fontWeight: 600 }}>
                  {endsOn ? longDate(endsOn) : 'the end of the period'}
                </b>
                , then posting stops. Your photos, brand DNA and drafts stay in your account.
              </span>
            </div>
            <ErrorText>{billingError}</ErrorText>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, paddingTop: 4 }}>
              <button type="button" className="btn-pill lg hv" onClick={() => setCancelOpen(false)}>
                Keep {sub.planName}
              </button>
              <button
                type="button"
                className="btn btn-primary hv"
                style={{ padding: '9px 16px', fontSize: 13 }}
                disabled={busy === 'cancel'}
                onClick={() =>
                  void runBilling(
                    'cancel',
                    billingApi.cancel,
                    'Cancelled · access continues until the period ends',
                  ).then((ok) => ok && setCancelOpen(false))
                }
              >
                Yes, cancel
              </button>
            </div>
          </div>
        </Overlay>
      )}
    </div>
  );
}
